"use client";

import type { JSX } from "react";
import { motion, type Variants } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "../ui/button";
import { SPECIALIST_URL } from "@/lib/consultation-links";

const ASSET_PREFIX = "/hong-kong-company-formation-v2";

const services1 = [
  {
    title: "Financial Statements",
    description:
      "We manage your company formation and prepare the required incorporation filing.",
    image: `${ASSET_PREFIX}/group-144.png`,
    imageClassName: "left-0 bottom-px h-[195px] w-[275px]",
  },
  {
    title: "Registered Hong Kong Address",
    description:
      "Your company needs a registered office located in HK. We arrange the required address as part of the applicable setup.",
    image: `${ASSET_PREFIX}/group-145.png`,
    imageClassName: "bottom-0 left-0 h-[171px] w-[302px]",
  },
  {
    title: "Incorporation Documents",
    description:
      "We manage the documents required for forming your company, including the incorporation application and Articles of Association.*",
    documentIllustration: true,
  },
  {
    title: "Business Registration",
    description:
      "Hong Kong operates a one-stop company incorporation and business registration process",
    image: `${ASSET_PREFIX}/group-150.png`,
    imageClassName: "left-9 top-[170px] h-[185px] w-[322px]",
  },
  {
    title: "Business Banking",
    description:
      "Depending on your selected package, we help set up your business banking so your new company has somewhere to receive and manage funds.",
    image: `${ASSET_PREFIX}/group-151.png`,
    imageClassName: "bottom-0 left-1/2 h-40 w-[223px] -translate-x-1/2",
  },
  {
    title: "Payment Processor Setup",
    description:
      "Our higher packages can include assistance with PayPal, Payoneer, Oceanpayments, etc. suited to established e-commerce sellers.",
    image: `${ASSET_PREFIX}/group-152.png`,
    imageClassName: "bottom-0 left-[69px] h-40 w-[227px]",
  },
];

type Service = {
  title: string;
  description: string;
  hasFootnote?: boolean;
  image?: {
    src: string;
    className: string;
    alt: string;
  };
  cta?: boolean;
};

const services: Service[] = [
  {
    title: "Financial Statements",
    description:
      "Financial statements preparation for the financial year, built to HKFRS or HK SME-FRS, whichever applies to your company.",
    image: {
      src: "/home-v3/group-144.png",
      className: "absolute bottom-0 left-0 z-20 h-[195px] w-[275px]",
      alt: "Company formation illustration",
    },
  },
  {
    title: "Management Reports",
    description:
      "You get detailed monthly management accounts and performance reporting, not just what is owed to the Inland Revenue Department, Hong Kong.",
    image: {
      src: "/home-v3/group-145.png",
      className: "absolute bottom-0 left-0 z-20 h-[171px] w-[302px]",
      alt: "Company secretary illustration",
    },
  },
  {
    title: "Reconciliation and\n Review",
    description:
      "We match your general ledger and recorded transactions against your bank and payment platform activity before it goes anywhere near an auditor.",
    hasFootnote: true,
    // illustration: "bookkeeping",
     image: {
      src: "/home-v3/group-146.png",
      className: "absolute bottom-0 left-10 z-20 h-[171px] w-[302px]",
      alt: "Company secretary illustration",
    },
  },
  {
    title: "Accounting Records\n Kept in Order",
    description:
      "Hong Kong companies are required to maintain proper accounting records for a defined period. So, we keep yours structured in your dashboard.",
    // illustration: "accounting",
      image: {
      src: "/home-v3/group-150.png",
      className: "absolute bottom-0 left-[30px] z-20 h-[191px] w-[282px]",
      alt: "Company secretary illustration",
    },
  },
  {
    title: "Handover Ready\n for Your Auditor",
    description:
      "When your financial year end arrives, your accounts are already in the shape an auditor expects.",
    image: {
      src: "/home-v3/group-151.png",
      className: "absolute bottom-0 left-[55px] z-20 h-[188px] w-[262px]",
      alt: "Tax filing illustration",
    },
  },
  {
    title: "Get our Hong Kong\nAccounting Services",
    description: "",
    cta: true,
  },
];

const cardsContainerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.18, delayChildren: 0.1 } },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 48 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.65, 0, 0.35, 1] },
  },
};

const headingVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

// Pre-rendered per-breakpoint screenshot of the glow (background blob + top
// shine), same rationale as FounderTrustBenefitsSection's CardGlow: this used
// to be a live CSS blur stack (137px + two 71.73/37.02px blurs) recomputed on
// every scroll frame across all 6 cards, which caused scroll jank. The shine's
// horizontal position is relative to the card ("calc(50% + 144px)"), and card
// width is genuinely fluid across the 1/2/3-column grid — so unlike the fixed
// card height (399px, exact at every breakpoint), the "2col" capture is a
// mid-range representative rather than pixel-exact across that whole range.
const glowStates = [
  { name: "1col", className: "block md:hidden" },
  { name: "2col", className: "hidden md:block xl:hidden" },
  { name: "3col", className: "hidden xl:block" },
] as const;

const Glow = () => (
  <>
    {glowStates.map((state) => (
      <img
        key={state.name}
        aria-hidden="true"
        alt=""
        src={`/hong-kong-company-formation-v2/generated/services-card-${state.name}.png`}
        className={`pointer-events-none absolute inset-0 h-full w-full object-cover ${state.className}`}
      />
    ))}
  </>
);

const CtaServiceCard = ({ service }: { service: Service }) => {
  return (
    <motion.div variants={cardVariants} className="transform-gpu will-change-transform">
      <Card className="relative flex h-[399px] w-full items-center justify-center overflow-hidden rounded-[32px] border-0 bg-[#0a0a0d] shadow-[0px_24px_60px_-30px_rgba(0,0,0,0.90)]">
        <span className="pointer-events-none absolute -right-[149px] -top-[61px] h-[326px] w-[184px] rounded-full bg-[#70a6ff] blur-[77px]" />
        <span className="pointer-events-none absolute -right-[149px] -top-[61px] h-[170px] w-[184px] rounded-full bg-white blur-[40px]" />
        <CardContent className="relative z-20 flex flex-col items-center gap-6 p-0 px-8 text-center">
          <h3 className="whitespace-pre-line [font-family:'Suisse_Works_Trial-Regular',Helvetica] text-2xl font-normal leading-[1.2] tracking-[-0.5px] text-white sm:text-[28px]">
            {service.title}
          </h3>
          <Button
            asChild
            className="relative inline-flex h-auto min-h-12 items-center justify-center whitespace-normal rounded-[40px] border-0 bg-[linear-gradient(180deg,#2444F2_0%,#3A54E6_50%,#A9B3E2_100%)] px-6 py-3 text-center [font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-sm font-normal leading-5 tracking-[0] text-white transition-colors hover:bg-[linear-gradient(180deg,#2444F2_0%,#3A54E6_50%,#A9B3E2_100%)]"
          >
            <a href={SPECIALIST_URL} target="_blank" rel="noopener noreferrer">
              Get our Hong Kong Accounting Services
            </a>
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const ServiceCard = ({ service }: { service: Service }) => {
  if (service.cta) {
    return <CtaServiceCard service={service} />;
  }

  return (
    <motion.div variants={cardVariants} className="transform-gpu will-change-transform">
      <Card className="relative h-[399px] w-full overflow-hidden rounded-[32px] border-0 bg-[#0f1218] shadow-none">
        <CardContent className="relative z-20 p-0">
          <div className="px-10 pt-6">
            <h3 className="mt-[-1px] whitespace-pre-line [font-family:'Suisse_Intl_Trial-Bold',Helvetica] text-2xl font-bold leading-7 tracking-[0] text-white">
              {service.title}
            </h3>
            <p className="mt-4 max-w-[312px] [font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-base font-normal leading-6 tracking-[0] text-white">
              {service.description}
              {service.hasFootnote && (
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger
                      type="button"
                      aria-label="Show incorporation document requirements"
                      className="ml-0.5 inline-flex cursor-pointer align-baseline font-bold text-[#a9b3e2] underline decoration-[#a9b3e2]/60 underline-offset-2 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#a9b3e2]"
                    >
                      *
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      align="start"
                      sideOffset={8}
                      className="relative w-[min(360px,calc(100vw-32px))] overflow-visible rounded-[20px] border border-white/10 bg-[#0f1218] p-5 text-white shadow-[0_18px_50px_rgba(0,0,0,0.45)] after:absolute after:-bottom-2 after:left-0 after:h-0 after:w-0 after:border-l-[10px] after:border-r-[10px] after:border-t-[10px] after:border-l-transparent after:border-r-transparent after:border-t-[#0f1218] after:content-['']"
                    >
                      <Badge className="mx-auto block w-fit rounded-[6px] border-0 bg-[#a9b3e2]/20 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#a9b3e2]">
                        Important information
                      </Badge>
                      <p className="mt-4 text-center [font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-sm leading-5 text-white/85">
                        The Hong Kong Companies Registry requires incorporation documents including Form NNC1 for a company limited by shares, the Articles of Association and the relevant business registration notice.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </p>
          </div>
        </CardContent>
        <Glow />
        {service.image && (
          <img
            className={service.image.className}
            src={service.image.src}
            alt={service.image.alt}
          />
        )}
      </Card>
    </motion.div>
  );
};


export const CompanyFormationServicesSection = (): JSX.Element => {
 return (
    <section
      aria-labelledby="service-approach-title"
      className="mx-auto mt-20 sm:mt-32 flex w-full max-w-[1440px] flex-col items-center gap-[59px] px-4 sm:px-8 lg:px-[108px]"
    >
      <header className="flex w-full max-w-[937px] flex-col items-center">
        <Badge className="animate-revolving-border rounded-[30px] border-0 bg-[#6a6a6a80] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23.4px] tracking-[0] text-white backdrop-blur-[3.5px]">
          What's included
        </Badge>
        <motion.h2
          id="service-approach-title"
          className="mt-6 max-w-[937px] [font-family:'Suisse_Works_Trial-Regular',Helvetica] text-center text-[34px] font-normal leading-[1.15] tracking-[-1.92px] text-white [text-shadow:0px_4px_24px_#0000000d] sm:text-5xl sm:leading-[64px]"
          variants={headingVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.6 }}
        >
          Everything Your Hong Kong Accounts Need 
          <br className="hidden sm:block" />
        to Be Audit and Filing Ready
        </motion.h2>

        <motion.p
          className="mt-6 max-w-[937px] [font-family:'Suisse_Works_Trial-Regular',Helvetica] text-center text-lg font-normal leading-[1.5] tracking-[-0.96px] text-white [text-shadow:0px_4px_24px_#0000000d] sm:text-xl sm:leading-[32px]"
          variants={headingVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.6 }}
        >
       Preparing accounts for a Hong Kong company means more than totaling a spreadsheet <br className="hidden sm:block" />
once a year.
        </motion.p>
      </header>
      <motion.div
        className="grid w-full grid-cols-1 gap-x-4 gap-y-[25px] md:grid-cols-2 xl:grid-cols-3"
        variants={cardsContainerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.25 }}
      >
        {services.map((service) => (
          <ServiceCard key={service.title} service={service} />
        ))}
      </motion.div>

      
    </section>
  );
};
